<template>
  <div class="payment-modal">
    <div class="modal-body">
      <div class="modal-container">
        <img
          v-if="!hideClose"
          class="close"
          src="@/assets/images/icons/close.svg"
          alt="close"
          @click="close"
        />
        <h2 class="title">Checkout</h2>
        <CourseUnlock v-if="showCourseUnlock" />
        <div class="promo-code-section">
          <PromoCode
            v-if="promoCodeShow"
            :active-promo-codes="relevantAppliedPromoCodes"
          />
        </div>
        <div class="payment-tabs">
          <div class="payment-tab-container">
            <div class="tab-placeholder"></div>
            <div
              class="payment-tab stripe-tab"
              :class="{
                'selected-tab': currentPaymentTab === PAYMENT_METHOD.STRIPE,
              }"
              @click="changeToStripe"
            >
              <img
                src="@/assets/images/payment/payment_methods.png"
                alt="card payment"
              />
            </div>
          </div>
          <div class="tab-gap"></div>
          <div class="payment-tab-container">
            <div
              class="payment-tab paypal-tab"
              :class="{
                'selected-tab': currentPaymentTab === PAYMENT_METHOD.PAYPAL,
              }"
              @click="changeTab(PAYMENT_METHOD.PAYPAL)"
            >
              <img src="@/assets/images/payment/paypal.png" alt="paypal" />
            </div>
            <div class="tab-placeholder"></div>
          </div>
        </div>
        <SavedCardTab
          v-if="currentPaymentTab === PAYMENT_METHOD.STRIPE_SAVED_CARD"
          @extraAction="extraAction"
          @trackPaymentEvents="trackPaymentEvents"
          :redirect-path="redirectPath"
          :product-ids="productIds"
          :trial="trial"
          :btn-copy="btnCopy"
          :promo-codes="promoCodesForCheckout"
        />
        <StripeTab
          v-else-if="currentPaymentTab === PAYMENT_METHOD.STRIPE"
          @trackPaymentEvents="trackPaymentEvents"
          :redirect-path="redirectPath"
          @extraAction="extraAction"
          :trial="trial"
          :btn-copy="btnCopy"
          :product-ids="productIds"
          :promo-codes="promoCodesForCheckout"
        />
        <PaypalTab
          v-else
          @trackPaymentEvents="trackPaymentEvents"
          :error="error"
          @extraAction="extraAction"
          :paypal-query-string="paypalQueryString"
          :trial="trial"
          :product-ids="productIds"
          :promo-codes="promoCodesForCheckout"
        />
        <div class="help" v-if="!hideHelp">
          <img src="@/assets/images/icons/chat/chat_grey.svg" alt="chat" />
          Need help?&nbsp;
          <span class="link" @click="startHelpChat"
            >Start Live Chat Support</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from "vuex"
import PromoCode from "./PromoCode.vue"
import StripeTab from "./StripeTab"
import PaypalTab from "./PaypalTab"
import SavedCardTab from "@/components/payment/SavedCardTab.vue"
// import SubscriptionDetails from "./SubscriptionDetails"
import Lottie from "vue-lottie"
import { PRODUCT_TYPE } from "@/constants/payment"
import { PAYMENT_METHOD } from "@/store/payment/payment.constants"
import { filterRelevantPromoCodes } from "@/store/payment/payment.utils"

export default {
  props: {
    btnCopy: {
      type: String,
      default: "PROCEED",
    },
    promoCodeShow: {
      type: Boolean,
      default: true,
    },
    hideHelp: {
      type: Boolean,
    },
    hideClose: {
      type: Boolean,
    },
    redirectPath: {
      type: String,
      default: `${window.location.origin}/dashboard`,
    },
    showDetails: {
      type: Boolean,
      default: false,
    },
    initTab: {
      type: String,
    },
    hideBack: {
      type: Boolean,
    },
    paypalQueryString: {
      type: String,
    },
    trial: {
      type: Boolean,
      default: false,
    },
    products: {
      type: Array,
      require: true,
    },
    showCourseUnlock: Boolean,
  },
  components: {
    PromoCode,
    StripeTab,
    PaypalTab,
    // SubscriptionDetails,
    SavedCardTab,
    Lottie,
    CourseUnlock: () => import("@/components/payment/CourseUnlock.vue"),
  },
  data: function () {
    return {
      error: null,
      PAYMENT_METHOD,
    }
  },
  computed: {
    ...mapGetters("Store", [
      "getProductData",
    ]),
    ...mapGetters("Payment", [
      "appliedPromoCodes",
      "savedPaymentMethods",
      "currentPaymentTab",
    ]),
    price () {
      return this.selectedProduct.billing_details.regular.price.amount
    },
    productIds () {
      return this.products.map(product => product.id)
    },
    relevantAppliedPromoCodes () {
      const products = this.productIds.map(productId => this.getProductData(productId))
      const promoCodes = this.appliedPromoCodes.map(appliedPromoCode => appliedPromoCode.promo_code)
      return filterRelevantPromoCodes(promoCodes, products)
    },
    promoCodesForCheckout () {
      return this.relevantAppliedPromoCodes.map(promoCode => {
        return promoCode.code
      })
    },
  },
  methods: {
    ...mapActions("Util", ["startHelpChat"]),
    ...mapActions("Optimizely", ["trackEvent"]),
    ...mapActions("Store", [
      "loadProductData",
    ]),
    ...mapActions("Payment", [
      "loadUserStripeCards",
      "fetchAppliedPromoCodes",
    ]),
    ...mapMutations("Payment", ["setPaymentError", "setCurrentPaymentTab"]),
    changeTab (tab) {
      this.setCurrentPaymentTab(tab)
    },
    changeToStripe () {
      if (this.savedPaymentMethods.length) {
        this.changeTab(PAYMENT_METHOD.STRIPE_SAVED_CARD)
      } else {
        this.changeTab(PAYMENT_METHOD.STRIPE)
      }
    },
    close () {
      this.$emit("closePaymentModal", false)
    },
    async trackStartedCheckout () {
      try {
        let checkoutInfo
        if (
          [
            PRODUCT_TYPE.MEMBERSHIP_SUBSCRIPTION,
            PRODUCT_TYPE.MEMBERSHIP_TRIAL,
            PRODUCT_TYPE.COACH_SUBSCRIPTION,
          ].includes(this.productType)
        ) {
          const price = this.selectedProduct.billing_details.trial
            ? this.selectedProduct.billing_details.trial.price.amount
            : this.selectedProduct.billing_details.regular.price.amount
          checkoutInfo = {
            $value: price,
            AddedItemProductName: this.selectedProduct.name,
            AddedItemPrice: price,
            AddedItemQuantity: 1,
            ItemNames: [this.selectedProduct.name],
            CheckoutURL: window.location.href,
            Items: [
              {
                ProductName: this.selectedProduct.name,
                Price: price,
                Quantity: 1,
              },
            ],
          }
        } else if (this.productType === PRODUCT_TYPE.POINT_PURCHASE) {
          checkoutInfo = {
            $value: this.selectedProduct.price,
            AddedItemProductName: `${this.selectedProduct.amount} Points`,
            AddedItemPrice: this.selectedProduct.price,
            AddedItemQuantity: 1,
            ItemNames: [`${this.selectedProduct.amount} Points`],
            CheckoutURL: window.location.href,
            Items: [
              {
                ProductName: `${this.selectedProduct.amount} Points`,
                Price: this.selectedProduct.price,
                Quantity: 1,
              },
            ],
          }
        } else {
          return
        }

        window._learnq.push(["track", "Started Checkout", checkoutInfo])
      } catch (error) {
        console.error(error)
      }
    },
    trackPaymentEvents () {
      try {
        if (
          [
            PRODUCT_TYPE.MEMBERSHIP_SUBSCRIPTION,
            PRODUCT_TYPE.MEMBERSHIP_TRIAL,
          ].includes(this.productType)
        ) {
          this.trackEvent("subscribe")
        }
      } catch (error) {}
    },
    checkPaypalError () {
      this.error = this.$route.query["error"]
      if (this.error) {
        this.changeTab("paypal")
      }
    },
    extraAction () {
      this.$emit("extraAction")
    },
  },
  async mounted () {
    // Clear state for a fresh checkout flow
    this.setPaymentError(null)

    // Load any data needed for the checkout
    await Promise.all([
      this.loadUserStripeCards(),
      this.fetchAppliedPromoCodes(),
      ...this.productIds.map(productId => this.loadProductData(productId)),
    ])

    // Handle any preloaded behaviors
    this.checkPaypalError()

    // Set initial payment option context
    if (this.initTab === "paypal") {
      this.setCurrentPaymentTab(PAYMENT_METHOD.PAYPAL)
    } else {
      if (this.savedPaymentMethods.length < 1) {
        this.setCurrentPaymentTab(PAYMENT_METHOD.STRIPE)
      } else {
        this.setCurrentPaymentTab(PAYMENT_METHOD.STRIPE_SAVED_CARD)
      }
    }

    // User behavior event logging
    this.trackAnalyticsEvent("View Purchase Modal")
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/_mixins.scss";
@import "@/assets/style/_variables.scss";
.payment-modal {
  position: relative;
  width: 100%;
  border-radius: 4px;
  overflow: auto;
  max-height: 100%;
  max-width: 50rem;
  font-family: $font-poppins;
  &::-webkit-scrollbar {
    display: none;
  }
  @include phone-down {
    margin-top: 0rem;
    max-width: 95%;
    max-height: 95%;
  }
  .modal-body {
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);

    .modal-container {
      padding-top: 2rem;
      background: white;
      border-radius: 4px 4px 0 0;

      .close {
        position: absolute;
        top: 2rem;
        right: 2rem;
        z-index: 21;
        opacity: 0.4;
        cursor: pointer;
      }
      .title {
        font-size: 1.6rem;
        font-weight: 600;
        text-align: center;
      }
      .description {
        font-size: 1.8rem;
        line-height: 2.8rem;
        display: flex;
        justify-content: space-between;
        padding: 0 4rem;
        margin-top: 1.2rem;
      }

      .promo-code-section {
        padding: 0 4rem;

        @include phone-small {
          padding: 0 2rem;
        }
      }

      .payment-tabs {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        margin: 1.9rem 0 3rem;
        @include phone-small {
          margin: 1rem 0 1.5rem;
        }
        .tab-gap {
          width: 1rem;
          height: 1px;
          flex: none;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        }
        .payment-tab-container {
          width: calc(50% - 0.5rem);
          display: flex;
          align-items: flex-end;
          .tab-placeholder {
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
            width: 4rem;
            flex: none;
          }
          .payment-tab {
            width: 100%;
            height: 4.9rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid rgba(0, 0, 0, 0.1);
            border-radius: 4px 4px 0 0;
            cursor: pointer;
            &.selected-tab {
              border-top: 3px solid #f42557;
              height: 5.1rem;
              border-bottom: none;
            }
          }
        }
      }
    }
    .help {
      background: #f0f0f0;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 4rem;
      margin-top: 2rem;
      color: #8a8a9a;
      font-size: 1.2rem;
      img {
        margin-right: 0.25rem;
      }
      .link {
        font-weight: 600;
        cursor: pointer;
        text-decoration: underline;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/payment/PaymentModal.vue